import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { openPaystackPopup } from "@/lib/paystack";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Clock, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

function timeLeft(expiresAt) {
  if (!expiresAt) return { text: "", expired: true };
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return { text: "expired", expired: true };
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  const text = days >= 1 ? `${days}d ${hours}h left` : hours >= 1 ? `${hours}h ${mins}m left` : `${mins}m left`;
  return { text, expired: false };
}

// Shared upgrade action — initializes Paystack ONLY on click, opens the inline popup.
export function useMembershipUpgrade() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const upgrade = async () => {
    setBusy(true);
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/payment/initialize", { callback_url });
      openPaystackPopup(data, {
        onSuccess: () => nav(`/payment/callback?reference=${data.reference}`),
        onCancel: () => { setBusy(false); toast.message("Payment not completed. You can activate anytime before your trial ends."); },
      });
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return { upgrade, busy };
}

export function TrialBanner() {
  const { user } = useAuth();
  const { upgrade, busy } = useMembershipUpgrade();
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);
  if (!user || user.role !== "trial") return null;
  const tl = timeLeft(user.trial_expires_at);
  return (
    <div data-testid="trial-banner" className="rounded-2xl border border-gold/40 bg-gradient-to-r from-gold/10 to-transparent p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-gold/15 grid place-items-center shrink-0"><Sparkles className="h-5 w-5 text-gold" /></div>
        <div>
          <p className="font-heading text-base font-bold flex items-center gap-2">
            You're on a 3-day free trial
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-gold"><Clock className="h-3 w-3" /> {tl.text}</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">Earn ₦100 per art review (2/day). Activate membership to withdraw earnings, collect artworks, fund your wallet and join auctions.</p>
        </div>
      </div>
      <Button onClick={upgrade} disabled={busy} data-testid="trial-activate-btn" className="rounded-full navy-gradient text-white font-semibold shrink-0">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate for ₦3,000"}
      </Button>
    </div>
  );
}
